// client/src/components/ReindexPanel.jsx
// Admin panel for Prof-iX content management.
// Usage:
//   import ReindexPanel from '../../components/ReindexPanel';
//   <ReindexPanel />

import React, { useState, useEffect } from 'react';
import useIndexedFile from '../../hooks/useIndexedFile';
import showNotification from '../../utilities/showNotification';
import CustomAlert from '../../components/customs/customAlert';
import './reindexPanel.css';

const ReindexPanel = () => {
  const {
    indexStatus,
    isReindexing,
    loadIndexStatus,
    reindexContent,
    reindexCourses,
    error,
    clearError,
  } = useIndexedFile();

  const [lastResult, setLastResult] = useState(null);
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    loadIndexStatus();
  }, []);

  const handleSmartReindex = async () => {
    clearError();
    setLastResult(null);
    const result = await reindexContent(false);
    setLastResult(result);
    if (result?.success) {
      showNotification(`${result.message}`, 'success');
      loadIndexStatus();
    } else {
      showNotification(`Reindex failed: ${result?.error}`, 'error');
    }
  };

  const handleCourseReindex = async () => {
    clearError();
    setLastResult(null);
    const result = await reindexCourses();   
    setLastResult(result);
    if (result?.success) {
      showNotification(`${result.message}`, 'success');
      loadIndexStatus();
    } else {
      showNotification(`Course reindex failed: ${result?.error}`, 'error');
    }
  };

  const handleForceReindex = () => {
    setShowAlert(true);
  };

  const handleAlertConfirm = async () => {
    setShowAlert(false);
    clearError();
    setLastResult(null);
    const result = await reindexContent(true);
    setLastResult(result);
    if (result?.success) {
      showNotification(`${result.message}`, 'success');
      loadIndexStatus();
    } else {
      showNotification(`Force reindex failed: ${result?.error}`, 'error');
    }
  };

  const handleAlertCancel = () => {
    setShowAlert(false);
  };

  return (
    <div className="ai-notebook-content" style={{background: 'white'}}>
      <div className="nb-inner-wrap">

        {/* ── Section 1: Title ── */}
        <div className="rp-title-section">
          <h3 className="rp-title">🤖 Prof-iX — Content Index</h3>
          <p className="rp-subtitle">
            Manage the knowledge base that Prof-iX uses to answer student questions.
          </p>
        </div>

        {/* ── Section 2: Stat Cards ── */}
        <div className="rp-section">
          <h4 className="rp-section-label">Overview</h4>
          {!indexStatus ? (
            <div className="rp-loading">
              <div className="loader" />
              <p>Loading index status...</p>
            </div>
          ) : (
            <div className="rp-stats-grid">
              <div className="rp-stat-card rp-stat-card--blue">
                <span className="rp-stat-value">{indexStatus.totalFiles}</span>
                <span className="rp-stat-label">Total Files Indexed</span>
              </div>
              <div className="rp-stat-card rp-stat-card--green">
                <span className="rp-stat-value">{indexStatus.totalChunks}</span>
                <span className="rp-stat-label">Total Chunks in DB</span>
              </div>
              <div className="rp-stat-card rp-stat-card--purple">
                <span className="rp-stat-value">
                  {[...new Set(indexStatus.files?.map(f => f.category) || [])].length}
                </span>
                <span className="rp-stat-label">Categories Indexed</span>
              </div>
            </div>
          )}
        </div>

        {/* ── Section 3: Breakdown by Source ── */}
        {indexStatus && (
          <div className="rp-section">
            <h4 className="rp-section-label">Breakdown by Source</h4>
            <div className="rp-source-grid">
              <div className="rp-source-card rp-source-card--amber">
                <span className="rp-source-icon">📖</span>
                <span className="rp-source-label">Ebook Chapters</span>
                <span className="rp-source-meta">
                  {indexStatus.bySource?.ebook?.files || 0} file{indexStatus.bySource?.ebook?.files !== 1 ? 's' : ''} · {indexStatus.bySource?.ebook?.chunks || 0} chunks
                </span>
              </div>
              <div className="rp-source-card rp-source-card--cyan">
                <span className="rp-source-icon">📋</span>
                <span className="rp-source-label">Platform READMEs</span>
                <span className="rp-source-meta">
                  {indexStatus.bySource?.platform?.files || 0} file{indexStatus.bySource?.platform?.files !== 1 ? 's' : ''} · {indexStatus.bySource?.platform?.chunks || 0} chunks
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── Section 4: Action Buttons ── */}
        <div className="rp-section">
          <h4 className="rp-section-label">Actions</h4>
          <div className="rp-actions">

            <button
              className="rp-btn rp-btn--primary"
              onClick={handleSmartReindex}
              disabled={isReindexing}
            >
              {isReindexing ? (
                <span className="rp-btn-loading">
                  <span className="loader rp-loader-sm" />
                  Indexing...
                </span>
              ) : (
                <span>
                  🔄 <strong>Smart Reindex</strong>
                  <span className="rp-btn-description">— only processes new or changed files</span>
                </span>
              )}
            </button>

            <button
              className="rp-btn"
              onClick={handleForceReindex}
              disabled={isReindexing}
            >
              🗑️ <strong>Force Reindex</strong>
              <span className="rp-btn-description">— deletes all vectors and rebuilds from scratch</span>
            </button>

            <button
              className="rp-btn"
              onClick={handleCourseReindex}
              disabled={isReindexing}
            >
              📚 <strong>Reindex Courses</strong>
              <span className="rp-btn-description">— syncs course data from MongoDB</span>
            </button>

            <button
              className="rp-btn"
              onClick={loadIndexStatus}
              disabled={isReindexing}
            >
              🔃 <strong>Refresh Status</strong>
            </button>

          </div>
        </div>

        {/* ── Section 5: Last Reindex Result ── */}
        {lastResult?.stats && (
          <div className="rp-section">
            <h4 className="rp-section-label">Last Reindex Result</h4>
            <div className={`rp-result ${lastResult.success ? 'rp-result--success' : 'rp-result--error'}`}>
              <strong>{lastResult.success ? '✅' : '❌'} {lastResult.message}</strong>
              <div className="rp-result-stats">
                <span>📥 Indexed: <strong>{lastResult.stats.indexed}</strong></span>
                <span>⏭️ Skipped: <strong>{lastResult.stats.skipped}</strong></span>
                <span>🗑️ Deleted: <strong>{lastResult.stats.deleted}</strong></span>
                <span>❌ Errors: <strong>{lastResult.stats.errors?.length || 0}</strong></span>
              </div>
              {lastResult.stats?.errors?.length > 0 && (
                <div className="rp-result-errors">
                  {lastResult.stats.errors.map((e, i) => (
                    <span key={i}>⚠️ {e}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Section 6: Error Banner ── */}
        {error && (
          <div className="rp-error-banner">❌ {error}</div>
        )}

        {/* ── Section 7: Hint ── */}
        <div className="rp-hint">
          <strong>💡 When to reindex:</strong><br />
          Use <strong>Smart Reindex</strong> after adding or editing <code>.md</code> files — skips unchanged files.<br />
          Use <strong>Force Reindex</strong> after renaming, moving, or deleting files.
        </div>

        {/* ── Section 8: Indexed Files List ── */}
        {indexStatus?.files?.length > 0 && (
          <div className="rp-section">
            <h4 className="rp-section-label">Indexed Files ({indexStatus.files.length})</h4>
            <div className="rp-files-list">
              {indexStatus.files.map((file, i) => (
                <div key={i} className="rp-file-row">
                  <span className="rp-file-name">
                    {file.source === 'platform' ? '📋' : '📖'} {file.filename}
                  </span>
                  <div className="rp-file-meta">
                    <span>{file.chunkCount} chunks</span>
                    <span>{file.category}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      <CustomAlert
        visible={showAlert}
        title="Force Reindex"
        icon="🗑️"
        message="This will delete ALL existing vectors and rebuild the knowledge base from scratch. This cannot be undone. Continue?"
        onConfirm={handleAlertConfirm}
        onCancel={handleAlertCancel}
      />
    
    </div>
  );
};

export default ReindexPanel;
